import { useEffect, useCallback, useState } from 'react'

export interface KeyboardShortcut {
  key: string
  modifiers?: {
    ctrl?: boolean
    shift?: boolean
    alt?: boolean
    meta?: boolean
  }
  action: () => void
  description: string
  category: 'playback' | 'navigation' | 'capture' | 'interface'
  enabled?: boolean
}

interface UseKeyboardShortcutsOptions {
  shortcuts: KeyboardShortcut[]
  enabled?: boolean
  preventDefault?: boolean
}

// Check if the event target is an input element
function isInputElement(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false
  const tagName = target.tagName.toLowerCase()
  return (
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select' ||
    target.isContentEditable
  )
}

// Check if modifiers match
function matchesModifiers(
  event: KeyboardEvent,
  modifiers?: KeyboardShortcut['modifiers']
): boolean {
  const ctrl = modifiers?.ctrl ?? false
  const shift = modifiers?.shift ?? false
  const alt = modifiers?.alt ?? false
  const meta = modifiers?.meta ?? false

  return (
    event.ctrlKey === ctrl &&
    event.shiftKey === shift &&
    event.altKey === alt &&
    event.metaKey === meta
  )
}

export function useKeyboardShortcuts({
  shortcuts,
  enabled = true,
  preventDefault = true,
}: UseKeyboardShortcutsOptions) {
  const [isHelpOpen, setIsHelpOpen] = useState(false)

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Skip if shortcuts are disabled
      if (!enabled) return

      // Skip if typing in an input
      if (isInputElement(event.target)) return

      // Handle help toggle (? key)
      if (event.key === '?' && !event.ctrlKey && !event.altKey && !event.metaKey) {
        event.preventDefault()
        setIsHelpOpen((prev) => !prev)
        return
      }

      // Find matching shortcut
      const matchingShortcut = shortcuts.find((shortcut) => {
        if (shortcut.enabled === false) return false
        const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase()
        const modifiersMatch = matchesModifiers(event, shortcut.modifiers)
        return keyMatches && modifiersMatch
      })

      if (matchingShortcut) {
        if (preventDefault) {
          event.preventDefault()
        }
        matchingShortcut.action()
      }
    },
    [shortcuts, enabled, preventDefault]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  return {
    isHelpOpen,
    setIsHelpOpen,
    shortcuts,
  }
}

// Format a shortcut key for display
export function formatShortcutKey(shortcut: KeyboardShortcut): string {
  const parts: string[] = []

  if (shortcut.modifiers?.ctrl) parts.push('Ctrl')
  if (shortcut.modifiers?.alt) parts.push('Alt')
  if (shortcut.modifiers?.shift) parts.push('Shift')
  if (shortcut.modifiers?.meta) parts.push('⌘')

  // Format special keys
  const keyMap: Record<string, string> = {
    ' ': 'Space',
    arrowleft: '←',
    arrowright: '→',
    arrowup: '↑',
    arrowdown: '↓',
    escape: 'Esc',
    enter: 'Enter',
    home: 'Home',
    end: 'End',
  }

  const displayKey = keyMap[shortcut.key.toLowerCase()] || shortcut.key.toUpperCase()
  parts.push(displayKey)

  return parts.join(' + ')
}

// Group shortcuts by category
export function groupShortcutsByCategory(
  shortcuts: KeyboardShortcut[]
): Record<string, KeyboardShortcut[]> {
  return shortcuts.reduce(
    (acc, shortcut) => {
      if (!acc[shortcut.category]) {
        acc[shortcut.category] = []
      }
      acc[shortcut.category].push(shortcut)
      return acc
    },
    {} as Record<string, KeyboardShortcut[]>
  )
}
